import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useTenants, useCreateTenant, useUpdateTenant } from '@/hooks/useTenants'
import {
    Building2, Plus, Edit, Globe, Zap,
    ShieldCheck, Loader2
} from 'lucide-react'
import type { Tenant } from '@/types/database'

export function TenantsPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [showForm, setShowForm] = useState(false)
    const [editing, setEditing] = useState<Tenant | null>(null)

    useEffect(() => { setPageTitle('Gestão de Entidades') }, [setPageTitle])

    const { data: tenants, isLoading } = useTenants()

    const filtered = tenants

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Associações & Entidades</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Gerenciamento multitenant das associações atendidas</p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true) }} className="btn btn-primary gap-2">
                    <Plus size={18} /> Nova Associação
                </button>
            </div>

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {isLoading ? (
                    <div className="col-span-full flex justify-center p-12">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : filtered?.length === 0 ? (
                    <div className="col-span-full text-center py-20 glass-card text-surface-700/60">
                        <Building2 size={40} className="mx-auto mb-3 opacity-20" />
                        <p>Nenhuma associação cadastrada</p>
                    </div>
                ) : (
                    filtered?.map(tenant => (
                        <div key={tenant.id} className="glass-card p-6 flex flex-col hover:shadow-xl transition-all duration-300 border-b-4"
                            style={{ borderBottomColor: (tenant.config as any)?.branding?.cor_primaria || '#6366f1' }}>
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center text-primary-600 shadow-inner">
                                    <Building2 size={24} />
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase
                                        ${tenant.ativo ? 'bg-green-100 text-green-700' : 'bg-surface-100 text-surface-500'}`}>
                                        {tenant.ativo ? 'Ativa' : 'Inativa'}
                                    </span>
                                    <button onClick={() => { setEditing(tenant); setShowForm(true) }}
                                        className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-primary-600 transition-colors">
                                        <Edit size={16} />
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-surface-900 leading-tight mb-1">{tenant.nome}</h3>
                            <p className="text-xs text-surface-700/60 font-medium mb-4">CNPJ: {tenant.cnpj}</p>

                            <div className="mt-auto pt-4 border-t border-surface-100 space-y-3">
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-surface-700/50 flex items-center gap-1.5 uppercase font-bold tracking-wider">
                                        <Zap size={14} className="text-yellow-500" /> API Asaas
                                    </span>
                                    <span className="font-mono text-surface-900">
                                        {(tenant.config as any)?.asaas_api_key ? '••••' + (tenant.config as any).asaas_api_key.slice(-4) : 'Não configurado'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-surface-700/50 flex items-center gap-1.5 uppercase font-bold tracking-wider">
                                        <Globe size={14} className="text-blue-500" /> Ambiente
                                    </span>
                                    <span className={`font-bold uppercase ${(tenant.config as any)?.asaas_ambiente === 'producao' ? 'text-green-600' : 'text-warning-600'}`}>
                                        {(tenant.config as any)?.asaas_ambiente || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal Form Placeholder (Simulado) */}
            {showForm && (
                <TenantForm
                    tenant={editing}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                />
            )}
        </div>
    )
}

function TenantForm({ tenant, onClose }: { tenant: Tenant | null; onClose: () => void }) {
    const createMut = useCreateTenant()
    const updateMut = useUpdateTenant()

    const [form, setForm] = useState({
        nome: tenant?.nome || '',
        cnpj: tenant?.cnpj || '',
        slug: tenant?.slug || '',
        asaas_api_key: (tenant?.config as any)?.asaas_api_key || '',
        asaas_ambiente: (tenant?.config as any)?.asaas_ambiente || 'sandbox',
        ativo: tenant?.ativo ?? true
    })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const payload = {
            nome: form.nome,
            cnpj: form.cnpj,
            slug: form.slug,
            config: {
                asaas_api_key: form.asaas_api_key,
                asaas_ambiente: form.asaas_ambiente
            }
        }

        if (tenant) {
            await updateMut.mutateAsync({ id: tenant.id, ...payload })
        } else {
            await createMut.mutateAsync(payload)
        }
        onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <form onSubmit={handleSubmit} className="bg-white w-full max-w-lg overflow-hidden flex flex-col animate-scale-in rounded-3xl shadow-elevated">
                <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                    <h3 className="font-bold text-surface-900">{tenant ? 'Editar Associação' : 'Nova Associação'}</h3>
                    <button type="button" onClick={onClose} className="p-1 hover:bg-surface-100 rounded-lg transition-colors">
                        <Plus size={20} className="rotate-45" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="label">Razão Social</label>
                            <input required className="input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
                        </div>
                        <div>
                            <label className="label">CNPJ</label>
                            <input required className="input" value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} />
                        </div>
                        <div>
                            <label className="label">Identificador (Slug)</label>
                            <input required className="input font-mono text-xs uppercase" placeholder="EX: ASSOCIACAO-SP"
                                value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} />
                        </div>
                    </div>

                    <div className="pt-4 border-t border-surface-100">
                        <p className="text-xs font-bold text-surface-400 uppercase mb-3 flex items-center gap-2">
                            <ShieldCheck size={14} /> Configuração Asaas (Gateway)
                        </p>
                        <div className="space-y-4">
                            <div>
                                <label className="label">API Key</label>
                                <input required className="input font-mono text-sm" value={form.asaas_api_key}
                                    onChange={e => setForm({ ...form, asaas_api_key: e.target.value })} />
                            </div>
                            <div>
                                <label className="label">Ambiente</label>
                                <select className="input" value={form.asaas_ambiente} onChange={e => setForm({ ...form, asaas_ambiente: e.target.value as any })}>
                                    <option value="sandbox">Sandbox (Testes)</option>
                                    <option value="producao">Produção</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-surface-100">
                        <label className="relative flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })}
                                className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                            />
                            <div>
                                <span className="font-semibold text-surface-900">Associação Ativa</span>
                                <p className="text-[10px] text-surface-500 font-medium">Define se esta entidade pode realizar operações no sistema.</p>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-surface-100 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="btn border border-surface-200 bg-white">Cancelar</button>
                    <button type="submit" className="btn btn-primary" disabled={createMut.isPending || updateMut.isPending}>
                        {createMut.isPending || updateMut.isPending ? <Loader2 className="animate-spin" size={18} /> : 'Salvar Associação'}
                    </button>
                </div>
            </form>
        </div>
    )
}
